import { Component, inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { BaseComponent } from '@shared/components/base.component';
import { SettingsService, SettingEventType } from '@modules/settings/shared/services/settings.service';
import { HttpError } from '@shared/interfaces/error.interface';
import { Config } from '@shared/configs/config';
import { IntilioCodes } from '@shared/enums/initilio-codes.enum';
import { MANAGEABLE_EMPLOYEE_ROLES } from '@shared/auth/enums/app-role.enum';
import { ButtonSize, ButtonTypes } from '@shared/modules/ui/components/button/button.component';
import { ConfirmationDiscardModalComponent } from '@shared/components/confirmation-discard-modal/confirmation-discard-modal.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Regex } from '@shared/configs/regex';
import { Contact } from '@modules/contacts/shared/models/contact.model';
import { NgSelectExtensionDirective } from '@shared/directives/ng-select-extension.directive';
import { ViewChild } from '@angular/core';

@Component({
  selector: 'app-team-add-employee',
  templateUrl: './team-add-employee.component.html',
  styles: [
    `
      .mat-mdc-dialog-content {
        overflow: initial;
      }

      ng-select.new-select ::ng-deep .ng-select-container .ng-value {
        white-space: nowrap !important;
      }
    `
  ]
})
export class TeamAddEmployeeComponent extends BaseComponent implements OnInit {
  form: UntypedFormGroup;
  ButtonTypes = ButtonTypes;
  ButtonSize = ButtonSize;
  Config = Config;

  public readonly dialogRef = inject(MatDialogRef<TeamAddEmployeeComponent>);
  public readonly service = inject(SettingsService);
  private readonly changes = inject(ChangeDetectorRef);
  private readonly dialog = inject(MatDialog);
  @ViewChild('emailSelect') private readonly emailSelect?: NgSelectExtensionDirective;

  positionSelectOptions = [];

  protected get contactsUrl(): string {
    return `${Config.API}/contact/search?emailsOnly=1`;
  }

  constructor() {
    super();

    this.dialogRef.disableClose = true;

    this.dialogRef.backdropClick().subscribe(() => {
      this.cancel();
    });
  }

  ngOnInit(): void {
    this.createForm();
    this.setPositions();
  }

  setPositions() {
    MANAGEABLE_EMPLOYEE_ROLES.forEach((key) => {
      this.positionSelectOptions.push({
        value: key,
        name: this.t.instant('ROLES.' + key)
      });
    });
  }

  createForm() {
    this.form = new UntypedFormGroup({
      email: new UntypedFormControl(null, [Validators.required]),
      role: new UntypedFormControl(null, [Validators.required])
    });
  }

  protected validateEmail(
    value: string
  ): { id: string; name: string; newItem: boolean; fullName: string; email: string } | null {
    if (!value?.length) return null;

    const email = value.toString().trim();
    if (!Regex.email.test(email)) {
      this.s.error(this.t.instant('FormErrors.email'));
      return null;
    }

    return {
      id: email,
      name: email,
      newItem: true,
      fullName: email,
      email
    };
  }

  protected successOnLoadContacts(
    response: { records?: Contact[] } | Contact[] | null | undefined
  ): Contact[] {
    const records = Array.isArray(response) ? response : response?.records || [];
    return records.map((contact) => new Contact(contact));
  }

  protected syncEmailDropdownPanelWidth(): void {
    requestAnimationFrame(() => {
      const selectElement = this.emailSelect?.select?.element;
      const trigger = selectElement instanceof HTMLElement ? selectElement : null;
      const panel = document.querySelector('ng-dropdown-panel.invite-email-select') as HTMLElement | null;

      if (!trigger || !panel) return;

      const rect = trigger.getBoundingClientRect();
      const viewportPadding = 16;
      const maxWidth = window.innerWidth - viewportPadding * 2;
      const width = Math.min(rect.width, maxWidth);
      const maxLeft = window.innerWidth - viewportPadding - width;
      const left = Math.min(Math.max(rect.left, viewportPadding), maxLeft);

      panel.style.width = `${width}px`;
      panel.style.minWidth = `${width}px`;
      panel.style.maxWidth = `${width}px`;
      panel.style.left = `${left}px`;
    });
  }

  submit() {
    this.form.markAllAsTouched();

    if (this.form.invalid || this.loading) {
      this.scrollToError();
      return;
    }
    this.loading = true;
    this.changes.detectChanges();
    this.service
      .inviteEmployee(this.form.value)
      .subscribe({
        next: this.onSuccessInviteEmployee.bind(this),
        error: this.onErrorInviteEmployee.bind(this)
      })
      .add(() => {
        this.loading = false;
        this.changes.detectChanges();
      });
  }

  onSuccessInviteEmployee() {
    this.s.success(this.t.instant('Settings.Team.employeeInvited'));
    this.service.emitter.emit({ type: SettingEventType.RELOAD_EMPLOYEES_LIST });
    this.form.reset();
    this.dialogRef.close();
  }

  onErrorInviteEmployee(e: HttpError) {
    switch (e.messageCode) {
      case IntilioCodes.EMPLOYE_ADDED:
        this.s.error(this.t.instant('Settings.Team.Errors.employeeAlreadyAdded'));
        break;
      case IntilioCodes.EMPLOYEE_EXISTS:
        this.s.error(this.t.instant('Settings.Team.Errors.employeeAlreadyExists'));
        break;
      default:
        this.s.error(this.t.instant('Settings.Team.Errors.errorInviteEmployee'));
        break;
    }
  }

  cancel(): void {
    if (this.form.pristine) {
      this.closeModal();
    } else {
      let dialogRef = this.dialog.open(ConfirmationDiscardModalComponent, {
        width: Config.DEFAULT_MODAL_WIDTH,
        autoFocus: false
      });

      dialogRef.afterClosed().subscribe((confirmed: boolean) => {
        switch (confirmed) {
          case true:
            {
              this.closeModal();
            }
            break;
          default:
            break;
        }
      });
    }
  }

  closeModal() {
    this.form.reset();
    this.dialogRef.close();
  }
}
